//@flow
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { identity, prop, propEq, curry, __ } from "ramda";
import { Maybe } from "ramda-fantasy";
import moment from "moment";

import { useI18n } from "@quantum/i18n";

import { Spinner } from "@quantum/ui";

import { useTable, useSortBy } from "react-table";

import CompartilharMedida from "../../../assets/compartilhar_medida.gif";
import ExcluirMedida from "../../../assets/excluir_medida.gif";

import { ErroCarregamento } from "../../comum";

import { FORMATO_DATA_HORA_EXIBICAO } from "../../../model";

import {
  getIdiomaUsuario,
  permiteCompartilhamentoDeArquivo,
} from "../../../state";

import { useArquivosMedidas } from "../../remote";

import { IoIosArrowRoundUp, IoIosArrowRoundDown } from "react-icons/io";

import "./lista-arquivos-medidas.css";

type Props = {
  handleSelecionarArquivo: any => any,
  handleCompartilharArquivo: any => any,
  handleExcluirArquivo: any => any,
};

const Header = ({
  column: {
    getHeaderProps,
    getSortByToggleProps,
    header,
    isSorted,
    isSortedDesc,
  },
}) => (
  <th
    {...getHeaderProps()}
    {...getSortByToggleProps()}
    className="ListaArquivosMedidas__header"
  >
    {header}
    <span className="ListaArquivosMedidas__header-ordenacao">
      {isSorted ? (
        isSortedDesc ? (
          <IoIosArrowRoundDown />
        ) : (
          <IoIosArrowRoundUp />
        )
      ) : (
        ""
      )}
    </span>
  </th>
);

const Cell = ({
  rowId,
  cell: { getCellProps, value },
  column: { formatter = identity, id },
}) => (
  <td
    id={rowId}
    {...getCellProps({
      className: `ListaArquivosMedidas__celula ListaArquivosMedidas__celula--${id}`,
    })}
  >
    {formatter(value)}
  </td>
);

const CellNome = ({
  rowId,
  cell: { getCellProps, value },
  column: { onClick },
}) => (
  <td
    id={rowId}
    onClick={onClick}
    {...getCellProps({
      className:
        "ListaArquivosMedidas__celula ListaArquivosMedidas__celula--nome",
    })}
  >
    {value}
  </td>
);

const formatarDataHora = (data: ?Date, locale: string) =>
  Maybe(data)
    .map(moment)
    .map(data =>
      locale
        ? data.locale(locale).format("L LT")
        : data.format(FORMATO_DATA_HORA_EXIBICAO)
    )
    .getOrElse("");

function ListaArquivosMedidas({
  handleSelecionarArquivo,
  handleCompartilharArquivo,
  handleExcluirArquivo,
}: Props) {
  const { translate } = useI18n();
  const [arquivosSelecionados, setArquivosSelecionados] = useState<Set<string>>(
    new Set()
  );
  const compartilhavel = useSelector(permiteCompartilhamentoDeArquivo);

  const idiomaUsuario = useSelector(getIdiomaUsuario);

  const {
    data: listaArquivos = [],
    loading,
    failed,
    fetchData,
  } = useArquivosMedidas({
    fetch: false,
    onLoad: useCallback(() => setArquivosSelecionados(new Set()), [
      setArquivosSelecionados,
    ]),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => void fetchData(), []);

  const onClickArquivo = useCallback(
    event =>
      handleSelecionarArquivo(
        listaArquivos.find(propEq("nome", event.target.id))
      ),
    [handleSelecionarArquivo, listaArquivos]
  );

  const onClickCompartilhar = useCallback(
    event =>
      handleCompartilharArquivo(
        listaArquivos.find(propEq("nome", event.target.id))
      ),
    [handleCompartilharArquivo, listaArquivos]
  );

  const onClickExcluir = useCallback(
    event => {
      const arquivo = listaArquivos.find(propEq("nome", event.target.id));
      if (
        arquivo &&
        window.confirm(
          translate("Arquivo.Msg.ConfirmacaoExclusaoArquivo", [arquivo.nome])
        )
      ) {
        handleExcluirArquivo(arquivo);
      }
    },
    [handleExcluirArquivo, listaArquivos, translate]
  );

  const onSelectArquivo = useCallback(event => {
    const { checked, id } = event.target;
    setArquivosSelecionados(arquivosSelecionados => {
      if (checked) {
        arquivosSelecionados.add(id);
      } else {
        arquivosSelecionados.delete(id);
      }
      return new Set([...arquivosSelecionados]);
    });
  }, []);

  const onClickSelecionarTodos = useCallback(
    () =>
      arquivosSelecionados.size === listaArquivos.length
        ? setArquivosSelecionados(new Set())
        : setArquivosSelecionados(new Set(listaArquivos.map(prop("nome")))),
    [arquivosSelecionados.size, listaArquivos]
  );

  const onClickExcluirMultiplos = useCallback(() => {
    if (window.confirm(translate("Arquivo.Msg.ExcluirTodosSelecionados"))) {
      [...arquivosSelecionados]
        .map(id => listaArquivos.find(propEq("nome", id)))
        .forEach(arquivo => handleExcluirArquivo(arquivo));
    }
  }, [arquivosSelecionados, handleExcluirArquivo, listaArquivos, translate]);

  const columnDefs = useMemo(
    () => [
      {
        header: `${translate("telas.arquivosExistentes")} (${
          listaArquivos.length
        })`,
        accessor: "nome",
        onClick: onClickArquivo,
        Cell: CellNome,
      },
      {
        header: translate("Comum.DataModificacao"),
        accessor: "dataModificacao",
        formatter: curry(formatarDataHora)(__, idiomaUsuario),
      },
    ],
    [listaArquivos.length, onClickArquivo, translate, idiomaUsuario]
  );

  const { columns, getTableBodyProps, rows, prepareRow } = useTable(
    {
      defaultColumn: {
        Cell,
        Header,
      },
      columns: columnDefs,
      data: listaArquivos,
    },
    useSortBy
  );

  if (loading) {
    return (
      <div className="ListaArquivosMedidas">
        <div className="ListaArquivosMedidas__spinner">
          <Spinner />
        </div>
      </div>
    );
  } else if (failed) {
    return <ErroCarregamento retry={fetchData} />;
  }

  return (
    <div className="ListaArquivosMedidas">
      <table
        {...getTableBodyProps({ className: "ListaArquivosMedidas__tabela" })}
      >
        <thead>
          <tr className="ListaArquivosMedidas__linha-header">
            <th className="ListaArquivosMedidas__header ListaArquivosMedidas__header--checkbox">
              <input
                type="checkbox"
                onChange={onClickSelecionarTodos}
                checked={listaArquivos.length === arquivosSelecionados.size}
              />
            </th>

            {columns.map((column, id) => column.render("Header", { key: id }))}

            {compartilhavel && (
              <th className="ListaArquivosMedidas__header ListaArquivosMedidas__header--compartilhar" />
            )}
            <th className="ListaArquivosMedidas__header ListaArquivosMedidas__header--excluir">
              <img
                onClick={onClickExcluirMultiplos}
                alt=""
                src={ExcluirMedida}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(
            (row, index) =>
              prepareRow(row) || (
                <tr
                  {...row.getRowProps({
                    className: "ListaArquivosMedidas__linha",
                  })}
                >
                  <td className="ListaArquivosMedidas__celula ListaArquivosMedidas__celula--checkbox">
                    <input
                      id={row.original.nome}
                      type="checkbox"
                      onChange={onSelectArquivo}
                      checked={arquivosSelecionados.has(row.original.nome)}
                    />
                  </td>

                  {row.cells.map((cell, id) =>
                    cell.render("Cell", { key: id, rowId: row.original.nome })
                  )}

                  {compartilhavel && (
                    <td className="ListaArquivosMedidas__celula ListaArquivosMedidas__celula--compartilhar">
                      <div className="ListaArquivosMedidas__celula--compartilhar--wrapper">
                        <img
                          id={row.original.nome}
                          onClick={onClickCompartilhar}
                          alt=""
                          src={CompartilharMedida}
                        />
                      </div>
                    </td>
                  )}

                  <td className="ListaArquivosMedidas__celula ListaArquivosMedidas__celula--excluir">
                    <img
                      id={row.original.nome}
                      onClick={onClickExcluir}
                      alt=""
                      src={ExcluirMedida}
                    />
                  </td>
                </tr>
              )
          )}
        </tbody>
      </table>
    </div>
  );
}

export default ListaArquivosMedidas;
